const comparable = (value) => JSON.stringify(value, (_key, child) => child && !Array.isArray(child) && typeof child === "object"
    ? Object.fromEntries(Object.entries(child).sort(([left], [right]) => left.localeCompare(right))) : child);

function listChanges(before, after) {
    const available = new Map(), matched = new Set(), positions = [], added = [];
    before.forEach((value, index) => {
        const key = comparable(value);
        if (!available.has(key)) available.set(key, []);
        available.get(key).push(index);
    });
    after.forEach((value) => {
        const index = available.get(comparable(value))?.shift();
        if (index === undefined) added.push(value);
        else { matched.add(index); positions.push(index); }
    });
    return { added, removed: before.filter((_value, index) => !matched.has(index)), unchanged: matched.size,
        reordered: positions.some((position, index) => index > 0 && position < positions[index - 1]) };
}

function ReadableValue({ value }) {
    if (typeof value === "string") return <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{value || "Empty text"}</span>;
    return <code className="whitespace-pre-wrap [overflow-wrap:anywhere]">{JSON.stringify(value, null, 2)}</code>;
}

function ValueList({ label, values, renderValue }) {
    if (!values.length) return null;
    return <div className="grid gap-[6px]">
        <p className="m-0 font-medium">{label} {values.length} {values.length === 1 ? "entry" : "entries"}</p>
        <ul aria-label={`${label} entries`} className="m-0 grid list-disc gap-[10px] pl-[18px]">
            {values.map((value, index) => <li key={index} className="min-w-0 pl-[2px]">{renderValue(value)}</li>)}
        </ul>
    </div>;
}

// Compare exact configuration values without rewriting user-authored text.
// Keep complete values available even when only changed list entries are shown.
export default function ConfigValueChange({ before, beforeFound, after, afterFound, renderValue = (value) => <ReadableValue value={value} /> }) {
    const arrays = (!beforeFound || Array.isArray(before)) && (!afterFound || Array.isArray(after));
    const text = (!beforeFound || typeof before === "string") && (!afterFound || typeof after === "string");
    const paragraphs = (value) => value ? value.split(/\r?\n\s*\r?\n/) : [];
    const changes = arrays ? listChanges(beforeFound ? before : [], afterFound ? after : [])
        : text ? listChanges(beforeFound ? paragraphs(before) : [], afterFound ? paragraphs(after) : []) : null;
    const equal = beforeFound === afterFound && comparable(before) === comparable(after);
    return <div className="grid min-w-0 gap-[10px] text-[13px] leading-[1.55]">
        {equal ? <p className="m-0 text-[color:var(--muted)]">Already matches the current policy.</p> : changes ? <>
            <ValueList label="Add" values={changes.added} renderValue={renderValue} />
            <ValueList label="Remove" values={changes.removed} renderValue={renderValue} />
            {changes.unchanged ? <p className="m-0 text-[12px] text-[color:var(--muted)]">{changes.unchanged} existing {changes.unchanged === 1 ? "entry" : "entries"} {changes.reordered ? "retained; order changed. Review the full values below." : "unchanged."}</p> : null}
            {!afterFound ? <p className="m-0 font-medium">Remove this setting.</p>
                : arrays && !after.length ? <p className="m-0 font-medium">Set to an empty list.</p>
                    : text && !after.length ? <p className="m-0 font-medium">Set to empty text.</p> : null}
        </> : <dl className="m-0 grid gap-[8px]">
            <div><dt className="text-[12px] text-[color:var(--muted)]">Current</dt><dd className="m-0">{beforeFound ? renderValue(before) : "Not set"}</dd></div>
            <div><dt className="text-[12px] text-[color:var(--muted)]">Proposed</dt><dd className="m-0">{afterFound ? renderValue(after) : "Remove this setting"}</dd></div>
        </dl>}
        <details className="min-w-0 text-[12px] text-[color:var(--muted)]">
            <summary className="cursor-pointer">Full current and proposed values</summary>
            <dl className="m-0 grid gap-[8px] pt-[8px]">{[
                ["Current", beforeFound, before], ["Proposed", afterFound, after],
            ].map(([label, found, value]) => <div key={label} className="min-w-0">
                <dt>{label}</dt><dd className="m-0"><pre aria-label={`${label} value`} className="m-0 max-h-[300px] overflow-auto whitespace-pre-wrap py-[4px] text-[11px] text-[color:var(--ink)] [overflow-wrap:anywhere]">{found ? JSON.stringify(value, null, 2) : "Not set"}</pre></dd>
            </div>)}</dl>
        </details>
    </div>;
}
